import {
  Ban,
  Check,
  ChevronRight,
  Circle,
  Loader2,
  RotateCcw,
  X,
} from 'lucide-react'
import { useState } from 'react'

import { cn } from '@/utils'

interface ToolCallBlockProps {
  name: string
  status: 'pending' | 'running' | 'ok' | 'error'
  errorCode?: string | null
  startedAt?: number | null
  finishedAt?: number | null
  argsSummary?: string | null
  resultSummary?: string | null
  // True when this errored attempt was retried successfully later in the turn.
  // Renders as a muted "retried" chip instead of a red failure.
  superseded?: boolean
  // Raw payloads shown inside the expanded panel. Optional so persisted
  // messages that no longer carry the original input/output (or live calls
  // mid-flight) still render — the expand button hides when there's nothing
  // to show.
  args?: unknown
  result?: unknown
}

const TOOL_LABELS: Record<string, string> = {
  get_project_context: 'Loading project context',
  query_transactions: 'Querying transactions',
  search_transactions_semantic: 'Searching transactions',
  aggregate_transactions: 'Aggregating transactions',
  compare_periods: 'Comparing periods',
  cash_flow_timeline: 'Reconstructing cash flow',
  get_existing_insights: 'Loading existing insights',
  read_skill: 'Reading skill',
  render_chart: 'Rendering chart',
  render_table: 'Rendering table',
  render_transactions: 'Rendering transactions',
  render_map: 'Rendering map',
  run_python: 'Running Python',
  export_transactions: 'Exporting transactions',
  preview_bulk_edit: 'Previewing bulk edit',
  apply_bulk_edit: 'Applying bulk edit',
  read_ai_report: 'Reading the AI report',
  propose_report_edit: 'Drafting report edit',
  apply_report_edit: 'Applying report edit',
  revert_report_edit: 'Reverting report edit',
  propose_case_fact: 'Drafting case fact',
  apply_case_fact: 'Saving case fact',
  tag_transactions: 'Tagging transactions',
  apply_filters: 'Updating filters',
  consult_external_accounts: 'Identifying recipients',
  consult_places_visited: 'Mapping locations',
  consult_deep_reasoning: 'Deep reasoning',
}

const VERB_PROGRESSIVE: Record<string, string> = {
  get: 'Getting',
  list: 'Listing',
  query: 'Querying',
  search: 'Searching',
  fetch: 'Fetching',
  read: 'Reading',
  render: 'Rendering',
  aggregate: 'Aggregating',
  compare: 'Comparing',
  load: 'Loading',
  create: 'Creating',
  update: 'Updating',
  delete: 'Deleting',
  run: 'Running',
  consult: 'Consulting',
}

export function humanizeToolName(name: string): string {
  const explicit = TOOL_LABELS[name]
  if (explicit) return explicit
  const parts = name.split('_').filter(Boolean)
  if (parts.length === 0) return name
  const [verb, ...rest] = parts
  const head = VERB_PROGRESSIVE[verb] ?? capitalize(verb)
  return [head, ...rest].join(' ')
}

function capitalize(word: string): string {
  if (!word) return word
  return word.charAt(0).toUpperCase() + word.slice(1)
}

function formatDuration(startedAt?: number | null, finishedAt?: number | null) {
  if (!startedAt || !finishedAt) return null
  const ms = Math.max(0, finishedAt - startedAt)
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

function isExpandablePayload(value: unknown): boolean {
  if (value == null) return false
  if (typeof value !== 'object') return true
  return Object.keys(value as Record<string, unknown>).length > 0
}

// Hard cap on how much pretty-printed JSON we render inline. render_table /
// render_transactions / run_python payloads can be megabytes — pasting them
// raw into a <pre> stalls the main thread and breaks scroll on mobile. The
// browser still has the full payload in the DraftAssistantMessage; we just
// stop drawing past the cap and tell the user how much was elided.
const PAYLOAD_CHAR_LIMIT = 4000
const ARRAY_ITEM_LIMIT = 50

function formatPayload(value: unknown): string {
  let serialized: string
  try {
    serialized = JSON.stringify(value, arrayCapReplacer(), 2)
  } catch {
    return String(value)
  }
  if (serialized.length <= PAYLOAD_CHAR_LIMIT) return serialized
  const elided = serialized.length - PAYLOAD_CHAR_LIMIT
  return `${serialized.slice(0, PAYLOAD_CHAR_LIMIT)}\n… ${elided.toLocaleString()} more chars elided`
}

// JSON.stringify replacer that caps each array at ARRAY_ITEM_LIMIT and appends
// a sentinel describing how many items were dropped. Caps each array
// independently, so a one-large-array payload elides at the rows level rather
// than character-truncating mid-row.
function arrayCapReplacer() {
  return (_key: string, val: unknown) => {
    if (!Array.isArray(val) || val.length <= ARRAY_ITEM_LIMIT) return val
    const omitted = val.length - ARRAY_ITEM_LIMIT
    return [
      ...val.slice(0, ARRAY_ITEM_LIMIT),
      `… ${omitted.toLocaleString()} more items elided`,
    ]
  }
}

export default function ToolCallBlock({
  name,
  status,
  errorCode,
  startedAt,
  finishedAt,
  argsSummary,
  resultSummary,
  superseded,
  args,
  result,
}: ToolCallBlockProps) {
  const [expanded, setExpanded] = useState(false)

  const label = humanizeToolName(name)
  const duration =
    status === 'ok' ? formatDuration(startedAt, finishedAt) : null
  const aborted = status === 'error' && errorCode === 'aborted'
  // An error the model recovered from by retrying. Shown muted, not red.
  const retried = status === 'error' && !aborted && Boolean(superseded)

  const hasArgs = isExpandablePayload(args)
  const hasResult = isExpandablePayload(result)
  const canExpand = (hasArgs || hasResult) && status !== 'pending'

  return (
    <div className="flex w-full min-w-0 flex-col gap-0.5">
      <button
        type="button"
        onClick={canExpand ? () => setExpanded((v) => !v) : undefined}
        disabled={!canExpand}
        aria-expanded={canExpand ? expanded : undefined}
        className={cn(
          'group inline-flex max-w-full items-center gap-1.5 self-start rounded-md border px-2 py-1 text-left text-xs transition-colors',
          canExpand && 'cursor-pointer',
          status === 'pending' &&
            'border-border bg-muted/30 text-muted-foreground',
          status === 'running' && 'border-blue-3/80 bg-white/50 text-blue-6',
          status === 'ok' &&
            'border-blue-3/80 bg-white/50 text-blue-6 hover:bg-blue-3/15',
          status === 'error' &&
            !aborted &&
            !retried &&
            'border-red-500/40 bg-red-500/[0.08] text-red-700 hover:bg-red-500/[0.12] dark:text-red-400',
          (aborted || retried) &&
            'border-border bg-muted/40 text-muted-foreground',
        )}
      >
        {status === 'pending' && (
          <Circle className="h-3 w-3 shrink-0 opacity-50" />
        )}
        {status === 'running' && (
          <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
        )}
        {status === 'ok' && (
          <Check className="h-3 w-3 shrink-0 text-emerald-600" />
        )}
        {aborted && <Ban className="h-3 w-3 shrink-0" />}
        {retried && <RotateCcw className="h-3 w-3 shrink-0" />}
        {status === 'error' && !aborted && !retried && (
          <X className="h-3 w-3 shrink-0" />
        )}

        <span className="shrink-0 font-medium">{label}</span>

        {argsSummary && (
          <span className="min-w-0 truncate text-[11px] opacity-60">
            {argsSummary}
          </span>
        )}

        {duration && (
          <span className="shrink-0 text-[10px] tabular-nums opacity-50">
            {duration}
          </span>
        )}

        {aborted && (
          <span className="shrink-0 text-[10px] opacity-70">(stopped)</span>
        )}

        {retried && (
          <span className="shrink-0 text-[10px] opacity-70">(retried)</span>
        )}

        {status === 'error' && !aborted && !retried && errorCode && (
          <span className="shrink-0 text-[10px] opacity-70">({errorCode})</span>
        )}

        {canExpand && (
          <ChevronRight
            className={cn(
              'ml-0.5 h-3 w-3 shrink-0 opacity-50 transition-transform group-hover:opacity-80',
              expanded && 'rotate-90',
            )}
            aria-hidden="true"
          />
        )}
      </button>

      {resultSummary && !expanded && (
        <p className="min-w-0 truncate pl-3 text-[11px] text-blue-6/80">
          {resultSummary}
        </p>
      )}

      {canExpand && expanded && (
        <div className="ml-2 flex min-w-0 flex-col gap-2 border-border/60 border-l py-1 pl-3 text-[11px]">
          {hasArgs && (
            <Section title="Input">
              <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono text-[10.5px] leading-snug text-muted-foreground">
                {formatPayload(args)}
              </pre>
            </Section>
          )}
          {hasResult && (
            <Section title="Output">
              <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono text-[10.5px] leading-snug text-muted-foreground">
                {formatPayload(result)}
              </pre>
            </Section>
          )}
        </div>
      )}
    </div>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
        {title}
      </p>
      {children}
    </div>
  )
}
